/**
 * Cell-level pieces for a tier price-list row (`TierPriceRow`, `GET /tiers/{id}/prices`).
 * No `<tr>` here — T065/T066's `pages/tiers/[tierId]/prices/columns.tsx` composes these into
 * `ColumnDef` cells, the same way `pages/catalogue/brands/columns.tsx` composes `DomainBadge`
 * inline. No arithmetic: only formats and badges values the API already returned (constitution I;
 * `packages/theme/src/guards.test.ts` check 3 polices this for real).
 */
import { Badge } from "@steelyard/theme";
import { formatMoney } from "../lib/format";
/**
 * `PriceSource` used to come from `CatalogComponents["schemas"]["PriceSource"]`. The catalog
 * OpenAPI document now describes it (and every other Catalog enum) as a bare `integer` even
 * though the service still serializes it with a snake_case `JsonStringEnumConverter` — verified
 * live against the running gateway 2026-09-14 (`GET /api/catalog/tiers` answers
 * `"priceRoundingMode":"none"`, a string, never `0`). Until that's fixed upstream, this file's
 * own hand-typed literal union in `lib/services/catalog.ts` is the one that matches the wire.
 */
import type { PriceSource } from "../lib/services/catalog";

export type { PriceSource };

/** Local shape mirroring `TierPriceRow`, narrowed to what these cells need (data-model §3). */
export type TierPriceRow = {
  rate: number | null;
  source: PriceSource;
};

/** FR-016: `from_discount` vs `manually_set` — icon plus colour, never colour alone (Badge's own doc). */
export function TierPriceOrigin({ source }: { source: PriceSource }) {
  return <Badge kind={source} />;
}

/** data-model §3: `rate: null` renders "not priced" — never `₹0.00`. */
export function TierPriceRateCell({ rate }: { rate: number | null }) {
  return <span>{formatMoney(rate)}</span>;
}

/**
 * T066: staleness badge, read straight from `isStale` — never derived from comparing `rate`
 * against a fresh base-price lookup (constitution I). Empty (not a "current" badge) when the row
 * isn't stale — the same "only say something when it's the exception" convention `tierStalenessColumns`'s
 * own `critical`/`manuallySet` flag cells use.
 */
export function TierPriceStaleBadge({ isStale }: { isStale: boolean }) {
  return isStale ? <Badge kind="stale" /> : null;
}
